import "maplibre-gl/dist/maplibre-gl.css";
import "./styles.css";
import { registerSW } from "virtual:pwa-register";
import {
  chartServer,
  localManifest,
  loadManifest,
  saveChartServer,
  saveLocalPacks,
  type PackDescriptor,
  type PackManifest,
} from "./storage/manifest";
import { installStates } from "./storage/installer";
import { opfsSupported } from "./storage/opfs";
import { ensurePackSearch, ensureStoredSearchIndexes } from "./storage/search";
import { PackRouter } from "./map/packRouter";
import { SetupScreen } from "./ui/setupScreen";
import { MapScreen } from "./ui/mapScreen";
import { ExportScreen } from "./ui/exportScreen";
import { ImportScreen } from "./ui/importScreen";
import { sharingKeyFromLocation } from "./sharing/crypto";
import { migrateLegacyStorage } from "./storage/migration";
import { initializeUiPreferences } from "./ui/preferences";
import { routeFromLocation, type RouteDefinition } from "./routes/routes";

const STYLE_URL = "style.json";

const app = document.querySelector<HTMLElement>("#app");
if (!app) throw new Error("Missing #app container");
let activeMapScreen: MapScreen | null = null;

initializeUiPreferences();
migrateLegacyStorage();
registerSW({ immediate: true });

function replaceScreen(element: HTMLElement): void {
  activeMapScreen?.stop();
  activeMapScreen = null;
  app!.replaceChildren(element);
}

function showMessage(title: string, body: string, tone: "warn" | "error" = "error"): void {
  const wrapper = document.createElement("div");
  wrapper.className = "setup";
  const inner = document.createElement("div");
  inner.className = "setup-inner";
  const heading = document.createElement("h1");
  heading.textContent = title;
  const notice = document.createElement("p");
  notice.className = "notice";
  notice.dataset.tone = tone;
  notice.textContent = body;
  inner.append(heading, notice);
  wrapper.append(inner);
  replaceScreen(wrapper);
}

/**
 * Opening over water the user has no detailed map for looks broken, so the
 * first view is centred on the regional packs actually installed. The world
 * background covers everything and would drag the centre into the Atlantic, so
 * it is excluded.
 */
function centreOfInstalledMaps(packs: PackDescriptor[]): [number, number] {
  if (!packs.length) return [0, 20];
  const charts = packs.filter((pack) => !pack.required);
  const source = charts.length ? charts : packs;
  let west = 180;
  let south = 90;
  let east = -180;
  let north = -90;
  for (const pack of source) {
    west = Math.min(west, pack.bounds[0]);
    south = Math.min(south, pack.bounds[1]);
    east = Math.max(east, pack.bounds[2]);
    north = Math.max(north, pack.bounds[3]);
  }
  return [(west + east) / 2, (south + north) / 2];
}

async function openMap(
  manifest: PackManifest,
  initialRoute: RouteDefinition | null = null,
): Promise<void> {
  const states = await installStates(manifest.packs);
  const installed = manifest.packs.filter((pack) => states.get(pack.id) === "installed");
  const searchable = await ensureStoredSearchIndexes(installed);

  const router = new PackRouter();
  await router.load(installed);
  if (import.meta.env.DEV) {
    (window as unknown as { __klintRouter?: PackRouter }).__klintRouter = router;
  }

  const screen = new MapScreen({
    styleUrl: STYLE_URL,
    router,
    fallbackCenter: centreOfInstalledMaps(installed),
    searchPacks: installed.filter((pack) => searchable.has(pack.id)),
    hasCatalogue: manifest.packs.length > 0,
    initialRoute,
    onMapServerChange: async (address) => {
      if (!address) {
        saveChartServer(null);
        await openMap(localManifest());
        return;
      }
      const refreshed = await connectMapServer(manifest, address);
      await openSetup(refreshed, true);
    },
    onManageCharts: () => void openSetup(manifest, true),
    onShareCharts: () => void openExport(manifest),
  });
  replaceScreen(screen.element);
  activeMapScreen = screen;
  await screen.start();
  if (chartServer() && searchable.size < installed.length) {
    screen.setSearchUpdating(true);
    void backfillSearchIndexes(installed, searchable, screen).finally(() => {
      screen.setSearchUpdating(false);
    });
  }
}

/**
 * Search indexes were introduced after people already had large map archives
 * installed. Keep startup network-free and render those maps immediately, then
 * use the configured server to enrich old descriptors and fetch only the small
 * missing sidecars in the background.
 */
async function backfillSearchIndexes(
  installed: PackDescriptor[],
  initiallyReady: Set<string>,
  screen: MapScreen,
): Promise<void> {
  try {
    const refreshed = await loadManifest({ allowFallback: false });
    const installedIds = new Set(installed.map((pack) => pack.id));
    const candidates = refreshed.packs
      .filter((pack) => installedIds.has(pack.id) && !!pack.search)
      // A regional chart index is small and contains the navigation-specific
      // objects people are most likely to look for. Make it useful before
      // importing the much larger world background index.
      .sort((a, b) => Number(!!a.required) - Number(!!b.required));
    const ready: PackDescriptor[] = [];
    const searchable = new Set(initiallyReady);
    const updated = new Map<string, PackDescriptor>();
    for (const pack of candidates) {
      try {
        if (await ensurePackSearch(pack)) {
          ready.push(pack);
          searchable.add(pack.id);
          updated.set(pack.id, pack);
          saveLocalPacks([pack]);
          screen.refreshSearchPacks(
            installed
              .map((candidate) => updated.get(candidate.id) ?? candidate)
              .filter((candidate) => searchable.has(candidate.id)),
          );
        }
      } catch {
        // This is opportunistic migration. Maps and existing indexes remain
        // usable when the remembered LAN server is currently unavailable.
      }
    }
    if (!ready.length) return;
  } catch {
    // Opening installed maps must never depend on the server being reachable.
  }
}

async function openSetup(manifest: PackManifest, canCancel: boolean): Promise<void> {
  const screen = new SetupScreen({
    manifest,
    canCancel,
    onDone: () => void openMap(manifest),
  });
  replaceScreen(screen.element);
  await screen.start();
}

async function openExport(manifest: PackManifest): Promise<void> {
  const screen = new ExportScreen({
    manifest,
    onCancel: () => void openMap(manifest),
  });
  replaceScreen(screen.element);
  await screen.start();
}

async function connectMapServer(
  fallback: PackManifest | null,
  address: string,
): Promise<PackManifest> {
  if (fallback) {
    const states = await installStates(fallback.packs);
    saveLocalPacks(
      fallback.packs.filter((pack) => states.get(pack.id) === "installed"),
    );
  }
  const manifest = await loadManifest({ server: address, allowFallback: false });
  saveChartServer(address);
  return manifest;
}

function clearSharingLink(): void {
  const url = new URL(location.href);
  url.hash = "";
  history.replaceState(null, "", url);
}

async function openImport(key: Uint8Array<ArrayBuffer>): Promise<void> {
  const screen = new ImportScreen({
    key,
    onDone: () => {
      clearSharingLink();
      void bootCharts();
    },
    onCancel: () => {
      clearSharingLink();
      void bootCharts();
    },
  });
  replaceScreen(screen.element);
  await screen.start();
}

async function bootCharts(initialRoute: RouteDefinition | null = null): Promise<void> {
  // Startup is deliberately network-free. A new browser gets an empty map;
  // installed/shared maps appear immediately; servers are connected through
  // the menu instead of blocking access to the browser.
  await openMap(localManifest(), initialRoute);
}

async function boot(): Promise<void> {
  if (!opfsSupported()) {
    showMessage(
      "This browser cannot store maps",
      "Klint needs the Origin Private File System to keep maps on your device. Use Safari 15.2 or later on iOS, or Chrome on Android.",
    );
    return;
  }

  try {
    const sharingKey = sharingKeyFromLocation();
    if (sharingKey) await openImport(sharingKey);
    else await bootCharts(routeFromLocation());
  } catch (error) {
    showMessage(
      "Could not start",
      error instanceof Error ? error.message : "Unknown error.",
    );
  }
}

void boot();
